/**
 * Casting-spell name label (#836) — the PURE, testable half.
 *
 * While a unit is casting a CAST-TIME spell, the world-space above-head stack
 * shows that spell's NAME just BELOW the bars (#878 — the slot #843 reserved
 * for it, moved under the cast bar so the whole stack, nameplate included,
 * sits closer to the model's head; see `BarStackOffsets.spellNameY`). This
 * module resolves the text; the Pixi
 * draw lives in `entityView.ts`, mirroring the `nameplate.ts`/`entityView.ts`
 * and `combatText.ts`/`combatTextManager.ts` splits so the mapping logic is
 * unit-tested without a Pixi/GL context.
 *
 * WHERE THE NAME COMES FROM: `cast.slot` (already on the wire,
 * `CAST_STATE_SCHEMA`) + the entity's `championId` (already resolved
 * client-side for the sprite/3D model/resource bar), fed through
 * `abilityLabels.ts`. Ability NAMES are deliberately NOT threaded over the
 * wire per-tick (`messages.ts`' "names/icons are resolved client-side"
 * contract) — this module must never reintroduce that. `abilityLabelResolved`
 * (not the plain `abilityLabel`) is used so the world label matches what the
 * caster's own action bar shows for a state-conditional slot (#76: Rogue's
 * Backstab slot reads "Ambush" while Stealth holds).
 *
 * CAST-TIME ONLY, FOR FREE: an INSTANT cast starts and resolves inside one
 * `step`, so `castState` is back to `idle` before the snapshot is serialized
 * (#804) — `cast` is `null` in every snapshot for an instant. Gating on
 * `cast !== null` therefore already excludes instants exactly as #836 asks;
 * there is no separate instant check anywhere here, and the `castFired` event
 * array (which exists BECAUSE instants have no observable cast state) is
 * deliberately not consulted.
 *
 * CUSTOMIZATION IS A HARD REQUIREMENT (mirrors `CombatTextStyle`): every
 * visual knob — colour, font size/weight, outline, truncation — lives on
 * `SpellNameStyle`; nothing in the draw path is a magic number.
 *
 * Determinism note: purely client-side cosmetics, derived from wire state the
 * client already has plus a build-time content-derived name table. No sim
 * state, no hash, no protocol change (`SIM_VERSION`/`PROTOCOL_VERSION`
 * untouched).
 */

import type { AuraDefId, ChampionId } from "@pit/protocol";
import { abilityLabelResolved } from "../champions/abilityLabels.js";

/**
 * Visual treatment for the world-space casting-spell name. One object, fully
 * data-driven — swap it to retheme with zero logic edits.
 */
export interface SpellNameStyle {
  /** Label colour (any CSS/Pixi colour string). */
  readonly color: string;
  /** Font size in px. Must stay under {@link SPELL_NAME_ROW_H} (the reserved
   *  row) or the label would grow into the nameplate above it — asserted in
   *  `spellNameLabel.test.ts`. */
  readonly fontSizePx: number;
  /** Font weight. */
  readonly fontWeight: number;
  /**
   * Dark outline colour. Load-bearing, not decoration: the style bible's
   * measured world palette (§3.7, #821) has a widest contrast of only 3.70:1
   * with adjacent steps at 1.45–1.68:1, so small text over the arena floor is
   * unreadable without an outline to separate it from whatever is behind it.
   */
  readonly outlineColor: string;
  /** Outline thickness in px (0 = no outline). */
  readonly outlinePx: number;
  /**
   * Maximum characters drawn before the name is truncated with
   * {@link SpellNameStyle.ellipsis}.
   *
   * LEGIBILITY DECISION (#836 scope 7): the label is CENTRE-anchored on the
   * unit and is ALLOWED TO EXCEED the 32px bar width — a name clipped to 32px
   * would be unreadable, and the bars themselves are the width reference, not
   * a container. Truncation is only a bound on pathologically long names: at
   * 22 every ability name shipped today (longest: "Curse of Exhaustion", 19)
   * renders in full, which `spellNameLabel.test.ts` pins across the whole
   * roster.
   */
  readonly maxChars: number;
  /** Suffix appended to a truncated name. */
  readonly ellipsis: string;
}

/** Default treatment. Tunable single source of truth (like combat text /
 *  nameplates): a neutral off-white, bold, dark-outlined so it reads over
 *  both the arena floor and a champion body. */
export const DEFAULT_SPELL_NAME_STYLE: SpellNameStyle = {
  color: "#f2f4f7",
  // #878: 10px. The label sits UNDER the cast bar now, inside a tightened
  // stack, so it is sized down with the bars; `SPELL_NAME_ROW_H` follows it
  // (10 × ~1.2 line box = 12).
  fontSizePx: 10,
  fontWeight: 700,
  outlineColor: "#0a0f14",
  outlinePx: 3,
  maxChars: 22,
  ellipsis: "…",
};

/**
 * Height in screen px reserved for the spell-name row in the above-head stack
 * (#836), the INNERMOST slot since #878 — directly BELOW the cast bar, sitting
 * on the stack base. RESERVED ALWAYS — like the resource bar's slot (#337),
 * the row is left EMPTY when there is no name to draw rather than collapsed.
 * That rule got MORE load-bearing when the row moved to the bottom: everything
 * else in the stack (all three bars AND the target-hotkey nameplate) sits on
 * top of it, so a row that collapsed while idle would drop the entire stack
 * `SPELL_NAME_ROW_H` px the instant a cast started and snap it back when the
 * cast ended.
 *
 * Sized to hold `DEFAULT_SPELL_NAME_STYLE.fontSizePx` plus the usual ~1.2×
 * line-box overshoot (#878: 10 × 1.2 = 12), the same way the bar heights next
 * to it in `entityView.ts` are fixed screen-space numbers.
 */
export const SPELL_NAME_ROW_H = 12;

/** The minimal cast-state shape this module reads — just the slot. Structural,
 *  so the wire `CastState` (and the interpolated view of it) satisfies it
 *  without this module importing the protocol's runtime schemas. */
export interface SpellNameCastState {
  readonly slot: number;
}

/**
 * `name` clipped to `style.maxChars`, ellipsised when it had to be cut. Pure.
 * A `maxChars` too small to fit the ellipsis degrades to a hard slice rather
 * than emitting an ellipsis-only label.
 */
export function truncateSpellName(name: string, style: SpellNameStyle): string {
  const max = style.maxChars;
  if (max <= 0) return "";
  if (name.length <= max) return name;
  const keep = max - style.ellipsis.length;
  return keep <= 0 ? name.slice(0, max) : name.slice(0, keep) + style.ellipsis;
}

/**
 * The label to draw under/over an entity's bar stack, or `null` to draw
 * NOTHING at all. Pure.
 *
 * `null` (no label, no box, no placeholder — and no layout shift, since the
 * row is reserved either way) when:
 *   • the entity is not casting (`cast === null`) — which is every idle unit
 *     AND every instant cast, see the module doc comment;
 *   • its champion isn't resolved yet (`championId === null`, same graceful
 *     absence the above-head resource bar uses); or
 *   • the slot has no ability name (unknown champion/slot) — never a "???"
 *     or an empty box.
 *
 * Works for every champion and every slot purely from `cast.slot` +
 * `championId`; there is no per-champion or per-ability branch here.
 */
export function castingSpellLabel(
  championId: ChampionId | null,
  cast: SpellNameCastState | null,
  selfAuras: readonly { readonly def: AuraDefId; readonly expiresAtTick: number | null }[],
  tick: number,
  style: SpellNameStyle,
): string | null {
  if (championId === null || cast === null) return null;
  const name = abilityLabelResolved(championId, cast.slot, selfAuras, tick);
  if (name === null) return null;
  const text = truncateSpellName(name, style);
  return text === "" ? null : text;
}
